import { BasePayload } from '@/analytics/fennel/types';
import {
  CreateOrderGuidePressPayload,
  ProspectFinderAccountPayload,
  ProspectFinderBusinessPayload,
  ProspectFinderCategoryFilterPayload,
  ProspectFinderClaimClickPayload,
  ProspectFinderMapSearchPayload,
  ProspectFinderPinCardClosePayload,
  ProspectFinderPriceFilterPayload,
  ProspectFinderSortPayload,
  ProspectFinderStatusFilterPayload,
  StartOrderPressPayload,
} from '@/experiences/sales-portal/analytics/fennel/types';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';

export interface SalesPortalAnalyticMetadata {
  description: string;
  eventName: string;
  origin?: string | string[];
}

export enum SalesPortalAnalyticEvent {
  CREATE_ORDER_GUIDE_ONLINE_MENU_PRESS = 'sales_portal.account_detail.create_order_guide.online_menu.press',
  CREATE_ORDER_GUIDE_PRESS = 'sales_portal.account_detail.create_order_guide.press',
  CREATE_ORDER_GUIDE_UPLOAD_PHOTO_PRESS = 'sales_portal.account_detail.create_order_guide.upload_photo.press',
  PROSPECT_FINDER_ACCOUNT_CREATION_CLICK = 'prospect_finder.account_creation_click',
  PROSPECT_FINDER_CATEGORY_FILTER_CLICK = 'prospect_finder.category_filter_click',
  PROSPECT_FINDER_ENABLE_LOCATION_CLICK = 'prospect_finder.enable_location_click',
  PROSPECT_FINDER_MAP_CLICK = 'prospect_finder.map_click',
  PROSPECT_FINDER_MAP_PIN_CLICK = 'prospect_finder.map_pin_click',
  PROSPECT_FINDER_MAP_SEARCH = 'prospect_finder.map_search',
  PROSPECT_FINDER_PIN_CARD_CLOSE_CLICK = 'prospect_finder.pin_card_close_click',
  PROSPECT_FINDER_PRICE_FILTER_CLICK = 'prospect_finder.price_filter_click',
  PROSPECT_FINDER_PROSPECT_ACCOUNT_CREATED = 'prospect_finder.prospect_account_created',
  PROSPECT_FINDER_RECENTER_CLICK = 'prospect_finder.recenter_click',
  PROSPECT_FINDER_RESULT_ROW_CLICK = 'prospect_finder.result_row_click',
  PROSPECT_FINDER_RETRY_CLICK = 'prospect_finder.retry_click',
  PROSPECT_FINDER_SEARCH_THIS_AREA_CLICK = 'prospect_finder.search_this_area_click',
  PROSPECT_FINDER_SORT_CLICK = 'prospect_finder.sort_click',
  PROSPECT_FINDER_STATUS_FILTER_CLICK = 'prospect_finder.status_filter_click',
  PROSPECT_FINDER_VIEW_ACCOUNT_DETAILS_CLICK = 'prospect_finder.view_account_details_click',
  START_ORDER_PRESS = 'sales_portal.account_detail.start_order.press',
}

export interface SalesPortalAnalyticPayloads {
  [SalesPortalAnalyticEvent.CREATE_ORDER_GUIDE_ONLINE_MENU_PRESS]: CreateOrderGuidePressPayload;
  [SalesPortalAnalyticEvent.CREATE_ORDER_GUIDE_PRESS]: CreateOrderGuidePressPayload;
  [SalesPortalAnalyticEvent.CREATE_ORDER_GUIDE_UPLOAD_PHOTO_PRESS]: CreateOrderGuidePressPayload;
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_ACCOUNT_CREATION_CLICK]: ProspectFinderClaimClickPayload;
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_CATEGORY_FILTER_CLICK]: ProspectFinderCategoryFilterPayload;
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_ENABLE_LOCATION_CLICK]: BasePayload;
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_MAP_CLICK]: BasePayload;
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_MAP_PIN_CLICK]: ProspectFinderBusinessPayload;
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_MAP_SEARCH]: ProspectFinderMapSearchPayload;
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_PIN_CARD_CLOSE_CLICK]: ProspectFinderPinCardClosePayload;
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_PRICE_FILTER_CLICK]: ProspectFinderPriceFilterPayload;
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_PROSPECT_ACCOUNT_CREATED]: ProspectFinderAccountPayload;
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_RECENTER_CLICK]: BasePayload;
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_RESULT_ROW_CLICK]: ProspectFinderBusinessPayload;
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_RETRY_CLICK]: BasePayload;
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_SEARCH_THIS_AREA_CLICK]: BasePayload;
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_SORT_CLICK]: ProspectFinderSortPayload;
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_STATUS_FILTER_CLICK]: ProspectFinderStatusFilterPayload;
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_VIEW_ACCOUNT_DETAILS_CLICK]: ProspectFinderAccountPayload;
  [SalesPortalAnalyticEvent.START_ORDER_PRESS]: StartOrderPressPayload;
}

const PROSPECT_FINDER_ORIGIN = SALES_PORTAL_ROUTES.ProspectFinder;

export const salesPortalEventsMetadata: {
  [K in SalesPortalAnalyticEvent]: SalesPortalAnalyticMetadata;
} = {
  [SalesPortalAnalyticEvent.CREATE_ORDER_GUIDE_ONLINE_MENU_PRESS]: {
    description:
      'When the Online menu option is pressed in the Create order guide dropdown on the Account Detail screen',
    eventName: 'Pressed Create Order Guide Online Menu',
    origin: SALES_PORTAL_ROUTES.AccountDetail,
  },
  [SalesPortalAnalyticEvent.CREATE_ORDER_GUIDE_PRESS]: {
    description:
      'When the Create order guide dropdown is opened on the Account Detail screen',
    eventName: 'Pressed Create Order Guide',
    origin: SALES_PORTAL_ROUTES.AccountDetail,
  },
  [SalesPortalAnalyticEvent.CREATE_ORDER_GUIDE_UPLOAD_PHOTO_PRESS]: {
    description:
      'When the Upload photo or file option is pressed in the Create order guide dropdown on the Account Detail screen',
    eventName: 'Pressed Create Order Guide Upload Photo',
    origin: SALES_PORTAL_ROUTES.AccountDetail,
  },
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_ACCOUNT_CREATION_CLICK]: {
    description:
      'When the Claim button is pressed on a prospect (list row or pin card) in the Prospect Finder',
    eventName: 'Prospect Finder Account Creation Click',
    origin: PROSPECT_FINDER_ORIGIN,
  },
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_CATEGORY_FILTER_CLICK]: {
    description: 'When a category is toggled in the Prospect Finder filter row',
    eventName: 'Prospect Finder Category Filter Click',
    origin: PROSPECT_FINDER_ORIGIN,
  },
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_ENABLE_LOCATION_CLICK]: {
    description:
      'When the Enable Location button is pressed in the Prospect Finder location priming banner',
    eventName: 'Prospect Finder Enable Location Click',
    origin: PROSPECT_FINDER_ORIGIN,
  },
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_MAP_CLICK]: {
    description: 'When the Prospect Finder map first renders (entry point)',
    eventName: 'Prospect Finder Map Click',
    origin: PROSPECT_FINDER_ORIGIN,
  },
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_MAP_PIN_CLICK]: {
    description: 'When a business pin is clicked on the Prospect Finder map',
    eventName: 'Prospect Finder Map Pin Click',
    origin: PROSPECT_FINDER_ORIGIN,
  },
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_MAP_SEARCH]: {
    description:
      'When a location is submitted via the Prospect Finder search after selecting a place',
    eventName: 'Prospect Finder Map Search',
    origin: PROSPECT_FINDER_ORIGIN,
  },
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_PIN_CARD_CLOSE_CLICK]: {
    description: 'When the Prospect Finder pin card is closed',
    eventName: 'Prospect Finder Pin Card Close Click',
    origin: PROSPECT_FINDER_ORIGIN,
  },
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_PRICE_FILTER_CLICK]: {
    description:
      'When a price range is toggled in the Prospect Finder filter row',
    eventName: 'Prospect Finder Price Filter Click',
    origin: PROSPECT_FINDER_ORIGIN,
  },
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_PROSPECT_ACCOUNT_CREATED]: {
    description:
      'When a prospect account is successfully created from the Prospect Finder',
    eventName: 'Prospect Finder Prospect Account Created',
    origin: PROSPECT_FINDER_ORIGIN,
  },
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_RECENTER_CLICK]: {
    description:
      'When the recenter / current-location control is pressed in the Prospect Finder',
    eventName: 'Prospect Finder Recenter Click',
    origin: PROSPECT_FINDER_ORIGIN,
  },
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_RESULT_ROW_CLICK]: {
    description:
      'When a business row is pressed in the Prospect Finder results list',
    eventName: 'Prospect Finder Result Row Click',
    origin: PROSPECT_FINDER_ORIGIN,
  },
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_RETRY_CLICK]: {
    description:
      'When the retry button is pressed after a Prospect Finder load error',
    eventName: 'Prospect Finder Retry Click',
    origin: PROSPECT_FINDER_ORIGIN,
  },
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_SEARCH_THIS_AREA_CLICK]: {
    description:
      'When the "Search this area" pill is pressed in the Prospect Finder',
    eventName: 'Prospect Finder Search This Area Click',
    origin: PROSPECT_FINDER_ORIGIN,
  },
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_SORT_CLICK]: {
    description:
      'When a sort option is selected in the Prospect Finder filter row',
    eventName: 'Prospect Finder Sort Click',
    origin: PROSPECT_FINDER_ORIGIN,
  },
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_STATUS_FILTER_CLICK]: {
    description:
      'When the Existing / Prospect status filter is toggled in the Prospect Finder',
    eventName: 'Prospect Finder Status Filter Click',
    origin: PROSPECT_FINDER_ORIGIN,
  },
  [SalesPortalAnalyticEvent.PROSPECT_FINDER_VIEW_ACCOUNT_DETAILS_CLICK]: {
    description:
      'When the View details button is pressed on a claimed business in the Prospect Finder',
    eventName: 'Prospect Finder View Account Details Click',
    origin: PROSPECT_FINDER_ORIGIN,
  },
  [SalesPortalAnalyticEvent.START_ORDER_PRESS]: {
    description:
      'When the Start Order button is pressed on the Account Detail screen',
    eventName: 'Pressed Start Order',
    origin: SALES_PORTAL_ROUTES.AccountDetail,
  },
};
